import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { ChevronRight, Package, Search, Sparkles } from "lucide-react";
import type { ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import { creativesService } from "@/feature/ads/cretives/services/creatives.service";
import { dictionary, Language } from "@/utils/dictionary";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type ProductSelectionMode = "website" | "product";

interface Step1SelectProductProps {
  language: Language;
  onClose: () => void;
  onSelectProduct: (product: ProductInfoCreative) => void;
  onCreateNew?: () => void;
  titleText?: string;
  hideSubtitle?: boolean;
  headerIcon?: "sparkles" | "package";
  hideFooterCancel?: boolean;
  selectionMode?: ProductSelectionMode;
}

let productSessionCache: ProductInfoCreative[] | null = null;
let productSessionInFlight: Promise<ProductInfoCreative[]> | null = null;

async function fetchProductsWithSessionCache(forceRefresh = false) {
  if (!forceRefresh && productSessionCache) return productSessionCache;
  if (!forceRefresh && productSessionInFlight) return productSessionInFlight;

  const request = creativesService.listProducts().then((data) => {
    productSessionCache = data;
    return data;
  });

  if (!forceRefresh) {
    productSessionInFlight = request;
    request.finally(() => {
      if (productSessionInFlight === request) productSessionInFlight = null;
    });
  }

  return request;
}

function normalizeProductName(value?: string) {
  return value?.trim().toLowerCase().replace(/\s+/g, " ") ?? "";
}

function normalizeProductImageUrl(value?: string) {
  return value?.trim().toLowerCase() ?? "";
}

function productNameKey(product: ProductInfoCreative) {
  return normalizeProductName(product.name);
}

function productVisualKey(product: ProductInfoCreative) {
  const name = normalizeProductName(product.name);
  const imageUrl = normalizeProductImageUrl(product.image_url);
  if (!name || !imageUrl) return null;
  return `${name}|${imageUrl}`;
}

function uniqueNonEmptyCount(values: Array<string | undefined | null>) {
  return new Set(values.map((value) => value?.trim()).filter(Boolean)).size;
}

function productScore(product: ProductInfoCreative) {
  const linkedWebsites = product.linked_websites_count ?? (product.website_id ? 1 : 0);
  return (
    linkedWebsites * 10 +
    (product.creatives?.length ?? 0) * 2 +
    (product.description?.trim() ? 1 : 0) +
    (product.image_url ? 1 : 0) +
    (product.website_id ? 1 : 0)
  );
}

function pickBestProduct(products: ProductInfoCreative[]) {
  return products.reduce((best, product) => (
    productScore(product) > productScore(best) ? product : best
  ));
}

function groupProductsForVideo(products: ProductInfoCreative[]) {
  const byProductId = new Map<string, ProductInfoCreative[]>();
  const withoutProductId: ProductInfoCreative[] = [];

  products.forEach((product) => {
    const productId = product.product_id?.trim();
    if (!productId) {
      withoutProductId.push(product);
      return;
    }

    const group = byProductId.get(productId) ?? [];
    group.push(product);
    byProductId.set(productId, group);
  });

  const productRows = Array.from(byProductId.values()).map((group) => {
    const representative = pickBestProduct(group);
    return {
      ...representative,
      linked_websites_count: uniqueNonEmptyCount(group.map((product) => product.website_id)),
      duplicate_products_count: 1,
    };
  });

  const rows = [...productRows, ...withoutProductId];
  const byProductName = new Map<string, ProductInfoCreative[]>();
  const withoutProductName: ProductInfoCreative[] = [];

  rows.forEach((product) => {
    const key = productNameKey(product);
    if (!key) {
      withoutProductName.push(product);
      return;
    }

    const group = byProductName.get(key) ?? [];
    group.push(product);
    byProductName.set(key, group);
  });

  return [
    ...Array.from(byProductName.values()).map((group) => {
      const representative = pickBestProduct(group);
      const linkedWebsites = group.reduce(
        (total, product) => total + (product.linked_websites_count ?? (product.website_id ? 1 : 0)),
        0
      );
      const duplicateProducts = uniqueNonEmptyCount(group.map((product) => product.product_id));

      return {
        ...representative,
        linked_websites_count: linkedWebsites,
        duplicate_products_count: Math.max(duplicateProducts, 1),
      };
    }),
    ...withoutProductName,
  ];
}

export function Step1SelectProduct({
  language,
  onClose,
  onSelectProduct,
  onCreateNew,
  titleText,
  hideSubtitle = false,
  headerIcon = "sparkles",
  hideFooterCancel = false,
  selectionMode = "website",
}: Step1SelectProductProps) {
  const [products, setProducts] = useState<ProductInfoCreative[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadProducts = async () => {
      const cachedProducts = productSessionCache;
      if (cachedProducts) {
        setProducts(cachedProducts);
        setLoading(false);
      } else {
        setLoading(true);
      }

      try {
        const data = await fetchProductsWithSessionCache(!!cachedProducts);
        if (cancelled) return;
        // Backend already returns products sorted by products.created_at DESC
        // (see WebsiteCreativeService.findProductsWithCreatives). The
        // previous client-side reverse() was a pre-deploy fallback that now
        // inverts a list that's already sorted the way we want — newest
        // first for both the image and the video "Seleccionar producto"
        // step.
        setProducts(data);
      } catch (error) {
        console.error("Error loading products:", error);
        if (!cancelled && !cachedProducts) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProducts();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleProducts = useMemo(() => (
    selectionMode === "product" ? groupProductsForVideo(products) : products
  ), [products, selectionMode]);

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return visibleProducts;
    const q = search.toLowerCase();
    return visibleProducts.filter((p) => p.name?.toLowerCase().includes(q));
  }, [visibleProducts, search]);

  const t = (key: string, fallback: string) =>
    dictionary[key]?.[language as Language] || fallback;
  const HeaderIcon = headerIcon === "package" ? Package : Sparkles;

  return (
    <>
      {/* Header */}
      <div className="p-6 pb-4 text-center border-b border-border/30">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
          style={{
            background: "linear-gradient(135deg, #4368E5 0%, #4368E5 100%)",
          }}
        >
          <HeaderIcon className="h-7 w-7 text-white" />
        </motion.div>

        <h2 className="text-xl font-bold text-foreground mb-1">
          {titleText ?? t("select-a-product", "Selecciona un Producto")}
        </h2>
        {!hideSubtitle ? (
          <p className="text-sm text-muted-foreground">
            {t(
              "select-the-product-for-which-you-want-to-create-the-ad",
              "¿Para cuál producto quieres crear el anuncio?"
            )}
          </p>
        ) : null}
      </div>

      {/* Product List */}
      <div className="p-4 space-y-2 max-h-[50vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">
              {t("loading-products", "Cargando productos...")}
            </div>
          </div>
        ) : (
          <>
            {/* TODO: Habilitar cuando el backend soporte POST /ads/creatives/products/{productId} */}
            {/* Create New Product — oculto temporalmente */}

            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("search-product", "Buscar producto...")}
                className={cn(
                  "w-full pl-9 pr-3 py-2.5 rounded-xl text-sm",
                  "bg-muted/30 border border-border/50",
                  "text-foreground placeholder:text-muted-foreground/50",
                  "focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20",
                  "transition-colors"
                )}
              />
            </div>

            {filteredProducts.length === 0 && !onCreateNew ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">
                  {t("no-products-available", "No hay productos disponibles")}
                </div>
              </div>
            ) : filteredProducts.length === 0 && search.trim() ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-muted-foreground/60">
                  {t("no-results", "Sin resultados para")} &ldquo;{search}&rdquo;
                </p>
              </div>
            ) : (
              filteredProducts.map((product, index) => {
                const linkedWebsites = product.linked_websites_count ?? 0;
                const duplicateProducts = product.duplicate_products_count ?? 1;
                const productMeta = selectionMode === "product"
                  ? [
                      linkedWebsites > 1
                        ? `${linkedWebsites} ${t("video-product-linked-funnels", "embudos conectados")}`
                        : null,
                      duplicateProducts > 1
                        ? `${duplicateProducts} ${t("video-product-grouped-versions", "versiones agrupadas")}`
                        : null,
                    ].filter(Boolean)
                  : [];
                const productKey = selectionMode === "product"
                  ? productNameKey(product) || product.product_id || productVisualKey(product) || `${product.website_id || index}`
                  : `${product.product_id}-${product.website_id}`;

                return (
                  <motion.button
                    key={productKey}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: (onCreateNew ? 0.1 : 0) + index * 0.05,
                    }}
                    onClick={() => onSelectProduct(product)}
                    type="button"
                    className={cn(
                      "w-full flex items-center gap-3 p-3 rounded-xl",
                      "border border-border/50 bg-card",
                      "hover:border-primary/50 hover:bg-primary/5",
                      "transition-all duration-200 text-left group"
                    )}
                  >
                    <div className="w-12 h-12 rounded-lg bg-white border border-border/30 overflow-hidden flex-shrink-0">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted/30" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground text-sm line-clamp-1 group-hover:text-primary transition-colors">
                        {product.name || t("product", "Producto")}
                      </h3>
                      {productMeta.length > 0 ? (
                        <p className="mt-0.5 text-xs text-muted-foreground/70 line-clamp-1">
                          {productMeta.join(" · ")}
                        </p>
                      ) : null}
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <ChevronRight className="h-4 w-4 text-primary" />
                    </div>
                  </motion.button>
                );
              })
            )}
          </>
        )}
      </div>

      {/* Footer */}
      {!hideFooterCancel ? (
        <div className="p-4 pt-0">
          <Button
            variant="ghost"
            className="w-full text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            {t("cancel", "Cancelar")}
          </Button>
        </div>
      ) : null}
    </>
  );
}
